import { type AxiosError } from 'axios'
import { useMutation, type UseMutationOptions } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { getEndpointWithParams } from 'queries/queries-utils'
import { serviceMutationKeys } from 'queries/services/query-keys'

import type { UpdateServicePayload } from 'queries/services/types'
import type { UserDefinedService } from 'types/api/services/service'

function updateService(
  queryKey?: ReturnType<typeof serviceMutationKeys.updateService>
) {
  return async (payload: Partial<UpdateServicePayload>) => {
    if (!queryKey)
      throw new Error('Unable to update a service without a valid serviceId')

    const [endpoint, params] = queryKey
    const endpointWithParams = getEndpointWithParams(endpoint, params)
    const response = await agent.patch<UserDefinedService>(
      endpointWithParams,
      payload
    )

    return response.data
  }
}

export function useUpdateService(
  serviceId?: string,
  options?: Omit<
    UseMutationOptions<
      UserDefinedService,
      AxiosError,
      Partial<UpdateServicePayload>
    >,
    'mutationFn' | 'mutationKey'
  >
) {
  const { currentTeamId: teamId } = useAuth()
  const mutationKey = serviceId
    ? serviceMutationKeys.updateService({ serviceId, teamId })
    : undefined

  return useMutation({
    ...options,
    mutationFn: updateService(mutationKey),
    mutationKey
  })
}
